import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import ArrowLeftImage from '../../../assets/icons/arrow_left.png';
import CatRunningCanvas from '../CatRunningCanvas';
import useCatRunningGame from '../useCatRunningGame';
import { createMockPlayStartResponse } from '../mockBackend';
import { loadCatRunningReplay } from '../replayStorage';
import { getCatRunningFrameReplayOptions } from '../replayPayload';

const AUTO_RESTART_DELAY_MS = 1200;

const formatDistance = (distance) => `${distance.toLocaleString('ko-KR')}m`;

function CatRunningReplayTestRun({ onRestart, replayOverride }) {
  const replayData = useMemo(
    () =>
      replayOverride ??
      loadCatRunningReplay() ?? {
        ...createMockPlayStartResponse({
          snackCount: 2,
          heartsRemaining: 2,
          preItems: [],
        }),
        events: [],
        result: {
          distanceM: 0,
          churuCount: 0,
          elapsedMs: 0,
          reviveUsed: false,
        },
      },
    [replayOverride],
  );
  const gameOptions = useMemo(
    () => {
      const localEventReplayAvailable = !replayOverride && Array.isArray(replayData.events) && replayData.events.length > 0;
      const serverReplayOptions = !localEventReplayAvailable
        ? getCatRunningFrameReplayOptions(replayData.replay)
        : null;
      const frameReplayOptions = serverReplayOptions ?? {
        frameReplay: localEventReplayAvailable ? undefined : replayData.frameReplay,
        frameReplayDurationSec: localEventReplayAvailable ? undefined : replayData.frameReplayDurationSec,
      };

      return {
        runSeed: replayData.runSeed,
        startStageKey: replayData.startStageKey,
        spawnManifest: replayData.spawnManifest,
        replayData: localEventReplayAvailable || !frameReplayOptions.frameReplay ? replayData : undefined,
        ...frameReplayOptions,
      };
    },
    [replayData, replayOverride],
  );
  const { hudState, renderState, worldRef, assetsRef, accumulatorRef, jump } = useCatRunningGame(gameOptions);

  useEffect(() => {
    if (hudState.gameStatus !== 'gameover') return undefined;

    const restartTimer = window.setTimeout(() => {
      onRestart();
    }, AUTO_RESTART_DELAY_MS);

    return () => {
      window.clearTimeout(restartTimer);
    };
  }, [hudState.gameStatus, onRestart]);

  const replayFrames = replayData.replay?.frames ?? replayData.frameReplay;
  const isFrameReplay = Array.isArray(replayFrames);

  const statusLabel = useMemo(() => {
    if (hudState.gameStatus === 'gameover') {
      return `${isFrameReplay ? '상대 리플레이 종료' : '충돌 감지'}, ${AUTO_RESTART_DELAY_MS / 1000}초 후 재시작`;
    }

    return isFrameReplay ? 'OPPONENT REPLAY RUNNING' : 'LAST SINGLE REPLAY RUNNING';
  }, [hudState.gameStatus, isFrameReplay]);

  return (
    <div className="min-h-screen bg-[#0C1821]">
      <div className="mx-auto flex min-h-screen w-full max-w-[500px] flex-col overflow-hidden bg-[#112431] shadow-[0_24px_80px_rgba(0,0,0,0.32)]">
        <div className="grid grid-cols-3 gap-2 bg-[#10212D] px-4 pb-3 pt-4 text-white">
          <div className="rounded-[16px] bg-white/8 px-3 py-3">
            <p className="m-0 font-['Pretendard'] text-[11px] font-bold uppercase tracking-[0.08em] text-white/65">
              Mode
            </p>
            <p className="m-0 mt-1 font-['Pretendard'] text-[20px] font-extrabold leading-none">
              {isFrameReplay ? 'BATTLE' : 'SINGLE'}
            </p>
          </div>
          <div className="rounded-[16px] bg-white/8 px-3 py-3">
            <p className="m-0 font-['Pretendard'] text-[11px] font-bold uppercase tracking-[0.08em] text-white/65">
              Distance
            </p>
            <p className="m-0 mt-1 font-['Pretendard'] text-[20px] font-extrabold leading-none">
              {formatDistance(hudState.distance)}
            </p>
          </div>
          <div className="rounded-[16px] bg-white/8 px-3 py-3">
            <p className="m-0 font-['Pretendard'] text-[11px] font-bold uppercase tracking-[0.08em] text-white/65">
              Churu
            </p>
            <p className="m-0 mt-1 font-['Pretendard'] text-[20px] font-extrabold leading-none">
              {hudState.churuCount}
            </p>
          </div>
        </div>

        <div className="relative flex-1 bg-[#DFF6FF]">
          <div className="relative overflow-hidden border border-black/10 bg-black/10">
            <CatRunningCanvas worldRef={worldRef} assetsRef={assetsRef} accumulatorRef={accumulatorRef} onJump={jump} />
          </div>

          {!renderState.uiReady && (
            <p className="m-0 px-3 pt-3 text-center font-['Pretendard'] text-[12px] font-bold text-[#36513C]">
              테스트 에셋 로딩 중...
            </p>
          )}
        </div>

        <div className="border-t border-white/10 bg-[#0E1C26] px-4 py-4 text-white">
          <div className="rounded-[14px] bg-white/8 px-4 py-3">
            <p className="m-0 font-['Pretendard'] text-[13px] font-bold text-[#8ED1FF]">리플레이 테스트 전용</p>
            <p className="m-0 mt-2 font-['Pretendard'] text-[14px] leading-[1.5] text-white/88">
              {statusLabel}
            </p>
            <p className="m-0 mt-1 font-['Pretendard'] text-[12px] leading-[1.5] text-white/58">
              runSeed {replayData.runSeed}
            </p>
            <p className="m-0 mt-1 font-['Pretendard'] text-[12px] leading-[1.5] text-white/58">
              현재는 서버 기준 `spawnManifest + runSeed`를 사용합니다.
            </p>
            <p className="m-0 mt-1 font-['Pretendard'] text-[12px] leading-[1.5] text-white/58">
              {isFrameReplay
                ? `상대 프레임 ${replayFrames.length}개 재생`
                : `입력 이벤트 ${replayData.events?.length ?? 0}개 재생`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CatRunningReplayTestScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [runKey, setRunKey] = useState(0);

  const battleReplay = useMemo(() => {
    const battleRound = location.state?.battleRound;
    if (!battleRound) return null;

    const opponentReplay = battleRound.opponent?.replay;
    const frameReplayOptions = getCatRunningFrameReplayOptions(opponentReplay);

    return {
      runSeed: battleRound.runSeed,
      startStageKey: battleRound.startStageKey,
      spawnManifest: battleRound.spawnManifest,
      ...frameReplayOptions,
    };
  }, [location.state]);

  return (
    <div className="relative">
      <div className="absolute left-0 right-0 top-0 z-20 mx-auto flex h-11 w-full max-w-[500px] items-center px-3">
        <button
          type="button"
          className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0"
          onClick={() => navigate('/cat-running')}
          aria-label="뒤로가기"
        >
          <img src={ArrowLeftImage} alt="" className="h-6 w-6 object-contain brightness-0 invert" />
        </button>
      </div>

      <CatRunningReplayTestRun
        key={runKey}
        replayOverride={battleReplay}
        onRestart={() => setRunKey((current) => current + 1)}
      />
    </div>
  );
}
